import { useDeferredValue, useEffect, useMemo } from 'react';
import { applyClassicalFill } from '~/features/watermark/classical';
import { removeBackgroundViaWorker } from '~/lib/bg-bridge';
import { useStore } from '~/store';
import type { RootState } from '~/store/slices';
import { removeBackground } from './remove';
import { buildRemoveBgOpts, selectRegionEffectMask } from './selectors';

/**
 * Synchronous selector — used by export / autodetect where we run once and
 * are happy to block. The live UI uses {@link useCleanedImageData} which
 * reads the worker-driven shared result from the store.
 */
export function selectCleanedImageData(state: RootState): ImageData | null {
  const { imageData } = state.source;
  if (!imageData) return null;
  let img = imageData;
  if (state.watermark.enabled && state.watermark.region) {
    img = applyClassicalFill(img, state.watermark.region);
  }
  const opts = buildRemoveBgOpts(state.background, selectRegionEffectMask(state));
  if (!opts) return img;
  return removeBackground(img, opts);
}

/**
 * Drives the cleaning pipeline. Mount once at app root — multiple mounts
 * would race against each other to write to the same store field.
 *
 * Watermark inpaint runs on the main thread (slow but rare; only when
 * source/region change). Background removal runs in a worker on every
 * tolerance change — `useDeferredValue` drops intermediate slider values so
 * we don't queue a worker round-trip per pixel.
 */
export function useCleanedPipeline(): void {
  // The matte pipeline drives the live preview in the matte step. In the
  // sprite step it pauses to free the worker; the only exception is a
  // post-reload rehydrate where we need one fresh run to repopulate
  // `workflow.committedCleaned` (which isn't persisted).
  const pipelineActive = useStore(
    (s) => s.workflow.step === 'matte' || s.workflow.committedCleaned === null,
  );
  const imageData = useStore((s) => s.source.imageData);
  const wmEnabled = useStore((s) => s.watermark.enabled);
  const wmRegion = useStore((s) => s.watermark.region);
  const bgEnabled = useStore((s) => s.background.enabled);
  const bgColors = useStore((s) => s.background.bgColors);
  const hardEdge = useStore((s) => s.background.hardEdge);
  const tolerance = useDeferredValue(useStore((s) => s.background.tolerance));
  const erode = useStore((s) => s.background.erode);
  const feather = useStore((s) => s.background.feather);
  const despillStrength = useStore((s) => s.background.despillStrength);
  const despillRadius = useStore((s) => s.background.despillRadius);
  const floodFill = useStore((s) => s.background.floodFill);
  const maxEnclosedIsland = useStore((s) => s.background.maxEnclosedIsland);
  const regionMask = useStore(selectRegionEffectMask);
  const setCleaned = useStore((s) => s.setCleaned);
  const setProcessing = useStore((s) => s.setProcessing);

  const inpainted = useMemo(() => {
    if (!pipelineActive) return null;
    if (!imageData) return null;
    if (!wmEnabled || !wmRegion) return imageData;
    return applyClassicalFill(imageData, wmRegion);
  }, [pipelineActive, imageData, wmEnabled, wmRegion]);

  useEffect(() => {
    if (!inpainted) {
      setCleaned(null);
      return;
    }
    const opts = buildRemoveBgOpts(
      {
        enabled: bgEnabled,
        bgColors,
        tolerance,
        hardEdge,
        erode,
        feather,
        despillStrength,
        despillRadius,
        floodFill,
        maxEnclosedIsland,
      },
      regionMask,
    );
    if (!opts) {
      setCleaned(inpainted);
      return;
    }
    let cancelled = false;
    setProcessing(true);
    removeBackgroundViaWorker(inpainted, opts).then(
      (r) => {
        if (!cancelled) {
          setCleaned(r);
          setProcessing(false);
        }
      },
      (err) => {
        if (cancelled) return;
        // Skip bg removal rather than running it sync — for large sheets the
        // sync path blocks the main thread for hundreds of ms, which is the
        // jank the worker exists to avoid.
        console.error('bg worker failed; showing un-keyed image', err);
        setCleaned(inpainted);
        setProcessing(false);
      },
    );
    return () => {
      cancelled = true;
      setProcessing(false);
    };
  }, [
    inpainted,
    bgEnabled,
    bgColors,
    tolerance,
    hardEdge,
    erode,
    feather,
    despillStrength,
    despillRadius,
    floodFill,
    maxEnclosedIsland,
    regionMask,
    setCleaned,
    setProcessing,
  ]);
}

/** Read the shared cleaned image. Mount {@link useCleanedPipeline} at app root first. */
export function useCleanedImageData(): ImageData | null {
  return useStore((s) => s.background.cleaned);
}
